import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Form } from 'react-final-form';
import { Button, Loader } from 'ncoded-component-library';
import dataRoomApi from 'router/subrouters/DataRoom/dataRoomApi';
import DataRoomContext from 'router/subrouters/DataRoom/providers/DataRoom/DataRoom.context';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import { DataRoomNode } from 'router/subrouters/DataRoom/models/DataRoomNode';
import CheckboxFieldGroup from 'components/CheckboxFieldGroup';
import {
  getCheckedValuesInObject,
  getIsArrayEmpty,
  getNodeMimeType,
} from 'utils';
import UnstarSelectAll from '../UnstarSelectAll';
import MymeTypeIcon from '../../../MymeTypeIcon';
import DataRoomDocumentsContext from '../../../../providers/DataRoomDocuments/DataRoomDocuments.context';
import mixpanelService from 'services/mixpanelService';
import useGetMixpanelProps from 'hooks/useGetMixpanelProps';
import { getMixpanelErrorProps } from 'axiosUtils';
import { ErrorViewedProps } from 'models/MixpanelEvents';
import { isMaximumSelected } from 'router/subrouters/DataRoom/utils';

import './UnstarDocumentsForm.styles.scss';

export type FormValues = {
  nodeIds: Record<string, boolean>;
};

type UnstarDocumentsFormProps = {
  className?: string;
  onClose: () => void;
  /** Notifies the caller which nodes were unstarred (e.g. to sync search results). */
  onUnstar?: (unstarredIds: string[]) => void;
};

const UnstarDocumentsForm: React.FC<UnstarDocumentsFormProps> = (props) => {
  const { className, onClose, onUnstar } = props;

  const [isLoading, setIsLoading] = useState(true);
  const [nodes, setNodes] = useState<DataRoomNode[]>([]);

  const {
    dataRoom: { _id },
  } = useContext(DataRoomContext);
  const { setCurrentlyDisplayedNodes } = useContext(DataRoomDocumentsContext);
  const { popServerError } = useContext(PopNotificationsContext);

  const classes = classNames('unstar-documents-form', className);

  const { t } = useTranslation();

  const getMixpanelProps = useGetMixpanelProps();

  const nodeOptions = useMemo(
    () =>
      nodes.map((node) => ({
        value: node._id,
        label: (
          <p className="node-group__option">
            <MymeTypeIcon
              mimeType={getNodeMimeType(node)}
              isLocked={isMaximumSelected(node?.fileSecurity)}
            />
            <p>{node.name}</p>
          </p>
        ),
      })),
    [nodes],
  );

  const onSubmit = useCallback(
    async ({ nodeIds }: FormValues) => {
      try {
        const selectedIds = getCheckedValuesInObject(nodeIds);

        if (getIsArrayEmpty(selectedIds)) return;

        await dataRoomApi.nodes.unstarDocuments(_id, selectedIds);

        setCurrentlyDisplayedNodes((old) =>
          old.map((el) =>
            selectedIds.includes(el._id) ? { ...el, isStarred: false } : el,
          ),
        );
        onUnstar?.(selectedIds);
        onClose();
      } catch (e) {
        popServerError(e);
      }
    },
    [_id, onClose, onUnstar, popServerError, setCurrentlyDisplayedNodes],
  );

  useEffect(() => {
    (async () => {
      try {
        setIsLoading(true);

        const { data } = await dataRoomApi.nodes.getStarredDocuments(_id);

        setNodes(data);
      } catch (e) {
        mixpanelService.errorViewed(
          getMixpanelProps(getMixpanelErrorProps(e)) as ErrorViewedProps,
        );
      } finally {
        setIsLoading(false);
      }
    })();
  }, [_id, getMixpanelProps]);

  if (isLoading) return <Loader />;

  return (
    <Form
      onSubmit={onSubmit}
      render={({ submitting, invalid, handleSubmit }) => (
        <form onSubmit={handleSubmit} className={classes}>
          {submitting && <Loader />}

          <UnstarSelectAll nodes={nodes} />

          <div className="node-group">
            <CheckboxFieldGroup required name="nodeIds" options={nodeOptions} />
          </div>

          <div className="actions">
            <Button variant="link" onClick={onClose}>
              {t('cancel')}
            </Button>

            <Button type="submit" disabled={submitting || invalid}>
              {t('done')}
            </Button>
          </div>
        </form>
      )}
    />
  );
};

export default UnstarDocumentsForm;
